import { useEffect, useState, useRef } from "react";
import { Bell } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";

const SEVERITY_DOT = {
  overdue: "bg-red-500",
  urgent: "bg-amber-500",
  upcoming: "bg-[#76C121]",
};

export default function NotificationBell() {
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const intervalRef = useRef(null);

  const load = async () => {
    try {
      const res = await api.get("/notifications");
      setAlerts(res.data);
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    load();
    intervalRef.current = setInterval(load, 60000);
    return () => clearInterval(intervalRef.current);
  }, []);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell-button"
          className="relative h-9 w-9 flex items-center justify-center rounded-md border border-[#E2E8F0] hover:bg-[#F8F9FA] transition-colors duration-200"
        >
          <Bell className="h-4 w-4 text-[#1C1C1E]" />
          {alerts.length > 0 && (
            <span
              data-testid="notification-count-badge"
              className="absolute -top-1.5 -right-1.5 h-4 min-w-[16px] px-1 rounded-full bg-[#00823B] text-white text-[10px] flex items-center justify-center font-semibold"
            >
              {alerts.length}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="px-4 py-3 border-b border-[#E2E8F0]">
          <h4 className="font-heading text-sm font-semibold text-[#1C1C1E]">Notifications</h4>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {alerts.length === 0 ? (
            <p className="text-sm text-[#4B5563] px-4 py-6 text-center">You're all caught up.</p>
          ) : (
            alerts.map((a, idx) => (
              <button
                key={idx}
                data-testid={`notification-item-${idx}`}
                onClick={() => {
                  setOpen(false);
                  if (a.type === "task_deadline") navigate("/tasks");
                  else if (a.type === "client_meeting" || a.type === "invoice_due") navigate("/clients");
                }}
                className="w-full text-left px-4 py-3 border-b border-[#E2E8F0] last:border-b-0 hover:bg-[#F8F9FA] transition-colors duration-200 flex items-start gap-2.5"
              >
                <span className={cn("h-2 w-2 rounded-full mt-1.5 shrink-0", SEVERITY_DOT[a.severity])} />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#1C1C1E] truncate">{a.title}</p>
                  <p className="text-xs text-[#4B5563] truncate">{a.message}</p>
                </div>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
